import React from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card, { CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  SsoQueryParamsTypes,
  SsoTypeTypes,
} from '~/components/SSO/utils/types';
import SSOLink from '~/components/SSOLink';
import useResponsive from '~/hooks/useResponsive';

interface ResponsiveActionCardProps extends Omit<CardProps, 'title'> {
  title: string | React.ReactNode;
  icon?: React.ReactNode | null;
  dataTestId?: string;
  backgroundColor?: string;
  buttonLabel?: string;
  onButtonClick?: (() => void) | undefined;
  children: React.ReactNode;
  actionMenu?: React.ReactNode;
  isButtonLoading?: boolean;
  isButtonDisabled?: boolean;
  actionButton?: React.ReactNode;
  href?: string;
  type?: string;
  queryParams?: SsoQueryParamsTypes;
  alertBannerForRestore?: React.ReactNode;
  fullWidthTitleSlot?: boolean;
}

interface StyledCardProps {
  actionMenu?: React.ReactNode;
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'actionMenu',
})<StyledCardProps>(({ actionMenu }) => ({
  '& .MuiCardHeader-action': {
    marginRight: actionMenu ? '-1.4rem' : 0,
  },
}));

const ResponsiveActionCard: React.FC<ResponsiveActionCardProps> = ({
  title,
  icon = null,
  buttonLabel = '',
  onButtonClick = undefined,
  children,
  isButtonLoading = false,
  isButtonDisabled = false,
  actionMenu = null,
  actionButton = null,
  href = undefined,
  type = undefined,
  queryParams = undefined,
  backgroundColor = '',
  dataTestId = 'responsive-action-card',
  alertBannerForRestore = null,
  fullWidthTitleSlot = false,
  ...props
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'responsiveActionCard',
  });

  const { sx, ...restProps } = props;
  const { isMobile } = useResponsive();

  const titleTypographySx = fullWidthTitleSlot
    ? ({ flex: 1, minWidth: 0 } as const)
    : undefined;

  const renderAction = () => {
    if (actionButton) return actionButton;
    if (actionMenu) return actionMenu;

    if (href) {
      return (
        <Button variant="outlined" href={href} color="primary">
          {t('buttonLabel')}
        </Button>
      );
    }

    if (type && queryParams) {
      return (
        <SSOLink
          type={type as SsoTypeTypes}
          queryParams={queryParams}
          variant="outlined"
          data-testid={`${dataTestId}-sso-link`}
        >
          {t('buttonLabel')}
        </SSOLink>
      );
    }

    if (onButtonClick) {
      return (
        <LoadingButton
          variant="outlined"
          loading={isButtonLoading}
          disabled={isButtonDisabled}
          onClick={onButtonClick}
          data-testid={`${dataTestId}-button`}
        >
          {buttonLabel || t('buttonLabel')}
        </LoadingButton>
      );
    }

    return null;
  };

  const renderActionForMobileView = () => {
    if (actionButton) return actionButton;

    if (href) {
      return (
        <Button fullWidth variant="outlined" href={href} color="primary">
          {t('buttonLabel')}
        </Button>
      );
    }

    if (type && queryParams) {
      return (
        <SSOLink
          type={type as SsoTypeTypes}
          queryParams={queryParams}
          variant="outlined"
          dataTestId={`${dataTestId}-sso-link`}
        >
          {t('buttonLabel')}
        </SSOLink>
      );
    }

    if (onButtonClick) {
      return (
        <LoadingButton
          variant="outlined"
          loading={isButtonLoading}
          disabled={isButtonDisabled}
          fullWidth
          onClick={onButtonClick}
          data-testid={`${dataTestId}-button`}
        >
          {buttonLabel || t('buttonLabel')}
        </LoadingButton>
      );
    }

    return null;
  };

  if (isMobile()) {
    return (
      <StyledCard
        data-testid={dataTestId}
        actionMenu={actionMenu}
        sx={{
          backgroundColor: backgroundColor,
          ...sx,
        }}
        {...restProps}
      >
        {alertBannerForRestore && alertBannerForRestore}
        <CardHeader
          disableTypography={fullWidthTitleSlot}
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              {icon && <Box>{icon}</Box>}
              <Typography variant="h3" sx={titleTypographySx}>
                {title}
              </Typography>
            </Stack>
          }
          action={actionMenu}
        />
        <CardContent>
          <Box
            sx={{
              pb: { xs: 1, md: 2 },
            }}
          >
            {children}
          </Box>
        </CardContent>
        <Stack pb={{ xs: 0, md: 2 }} px={{ xs: 0, md: 2 }}>
          {renderActionForMobileView()}
        </Stack>
      </StyledCard>
    );
  }

  return (
    <StyledCard
      data-testid={dataTestId}
      actionMenu={actionMenu}
      sx={{
        backgroundColor: backgroundColor,
        ...sx,
      }}
      {...restProps}
    >
      {alertBannerForRestore}
      <CardHeader
        disableTypography={fullWidthTitleSlot}
        title={
          <Stack direction="row" spacing={1} alignItems="flex-end">
            {icon !== null && icon}
            <Typography variant="h3" sx={titleTypographySx}>
              {title}
            </Typography>
          </Stack>
        }
        action={renderAction()}
      />
      <CardContent>
        <Box>{children}</Box>
      </CardContent>
    </StyledCard>
  );
};

export default ResponsiveActionCard;
